import { Component, OnDestroy, OnInit, inject } from '@angular/core';
import { injectUrlViewState } from '../../../utils/url-view-state.util';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Router, RouterLink } from '@angular/router';
import { MatDialog } from '@angular/material/dialog';
import { MatTooltipModule } from '@angular/material/tooltip';
import { ToastrService } from 'ngx-toastr';
import { NgxSpinnerService } from 'ngx-spinner';
import {
  Subject,
  Subscription,
  debounceTime,
  distinctUntilChanged,
  map,
  of,
  switchMap,
  timer,
} from 'rxjs';
import { RestaurantService } from '../../../services/restaurant.service';
import { ReservationService, isUnavailable } from '../../../services/reservation.service';
import { ConfirmationDialogComponent } from '../../controls/fnf-confirmation/confirmation-dialog/confirmation-dialog.component';
import { AddReservationModalComponent } from './modal/add-reservation-modal/add-reservation-modal.component';
import { RequestQueueComponent } from './requests/request-queue.component';
import { askDeclineReason } from '../../modals/decline-request-modal/decline-request-modal.component';
import { TourComponent, TourStep } from '../../controls/fnf-tour/tour.component';
import { PickerOnlyDateDirective } from '../../../directives/picker-only-date.directive';
import {
  US_PHONE_PATTERN,
  displayUsPhone,
  formatUsPhone,
  usPhoneDigits,
} from '../../../utils/us-phone.util';
import { isGuestName } from '../../../utils/guest-name.util';
import {
  SMS_CONSENT,
  MAX_STAFF_PARTY_SIZE,
  CHANNEL_ICONS,
  CHANNEL_LABELS,
  CLOSED_STATUSES,
  GuestLookup,
  RESERVATION_STATUS_LABELS,
  Reservation,
  ReservationChannel,
  ReservationSettings,
  ReservationStatus,
  ServicePeriod,
  ServingHoursState,
  StandingNotesChange,
  canTransition,
  declineReasonLabel,
  requestDeadlineLabel,
  ReservationTable,
} from '../../../models/reservation';
import {
  addMinutes,
  atTime,
  periodEnd,
  formatInZone,
  timeLabel,
  toServiceDate,
  turnTimeFor,
} from '../../../services/reservation-time.util';
import { dateLabel } from '../../../utils/serving-hours.util';
import {
  VisitBadge,
  allergyWarning,
  bookingForPhone,
  hasStandingNotes,
  historySupported,
  noShowLine,
  parseTags,
  returningGuestSummary,
  standingNotesChange,
  standingNotesDropped,
  visitBadge,
} from '../../../utils/guest-history.util';

const NOTES_NOT_KEPT =
  "The allergies and preferences weren't saved: this server doesn't keep guest notes yet.";

/** A booking change's text and email outcomes, as a toast for the host. */
export interface GuestNotifiedToast {
  ok: boolean;
  title: string;
  text: string;
}

/**
 * Professional wording for how the guest heard about a booking change, naming
 * where each message went. `contact` is the guest's email and formatted phone.
 */
export function guestNotifiedLine(
  sms: string | null | undefined,
  email: string | null | undefined,
  contact: { email?: string | null; phone?: string | null } = {},
): GuestNotifiedToast | null {
  if (!sms && !email) {
    return null; // not a change the guest is told about
  }
  const sent = (s: string | null | undefined) => s === 'sent' || s === 'duplicate';
  // Tried and didn't arrive: worth telling the host. Skipped / off are not.
  const missed = (s: string | null | undefined) =>
    !!s && ['failed', 'unavailable', 'invalid', 'opted_out'].includes(s);
  const toEmail = contact.email ? ` to ${contact.email}` : '';
  const toPhone = contact.phone ? ` to ${contact.phone}` : '';
  const textSent = sent(sms);
  const emailSent = sent(email);

  if (textSent && emailSent) {
    return {
      ok: true,
      title: 'Guest notified',
      text: `An email was sent${toEmail} and a text message${toPhone}.`,
    };
  }
  if (emailSent) {
    return missed(sms)
      ? {
          ok: false,
          title: 'Guest partly notified',
          text: `An email was sent${toEmail}, but the text message${toPhone} could not be delivered.`,
        }
      : { ok: true, title: 'Guest notified', text: `An email was sent${toEmail}.` };
  }
  if (textSent) {
    return missed(email)
      ? {
          ok: false,
          title: 'Guest partly notified',
          text: `A text message was sent${toPhone}, but the email${toEmail} could not be delivered.`,
        }
      : { ok: true, title: 'Guest notified', text: `A text message was sent${toPhone}.` };
  }
  if (missed(sms) || missed(email)) {
    return {
      ok: false,
      title: 'Guest not notified',
      text: "We couldn't reach the guest by email or text message. Please contact them directly.",
    };
  }
  return null; // nothing to send to, or notifications are off
}
/**
 * A timed booking can be seated from this long before its start until its
 * booked end. reservation-service enforces the same window (FNF-1132).
 */
const SEAT_EARLY_MINUTES = 60;

/** How far back and ahead the book's calendar reaches. */
const DATE_RANGE_DAYS = 365;

/** `requests`: every pending request across dates (FNF-308). */
export type ReservationView = 'book' | 'timeline' | 'waitlist' | 'requests';
export type ReservationStatusFilter =
  'all' | 'open' | 'pending' | 'confirmed' | 'seated' | 'closed';

interface BookGroup {
  label: string;
  items: Reservation[];
  covers: number;
}

interface TimelineSlot {
  start: Date;
  label: string;
  items: Reservation[];
  covers: number;
  /** 0..100+ share of the pacing cap used in this interval. */
  pacingPct: number;
}

/**
 * Host book: the day's reservations by service, a timeline by slot, and the
 * walk-in waitlist, with a detail panel for the selected party. Data comes
 * from `ReservationService` (`/v1/restaurants/reservations`).
 */
import { bookingTableNames, tableNamesLabel } from '../../../pipes/table-names.pipe';
@Component({
  selector: 'app-reservations',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    RouterLink,
    MatTooltipModule,
    TourComponent,
    PickerOnlyDateDirective,
    RequestQueueComponent,
  ],
  templateUrl: './reservations.component.html',
  styleUrl: './reservations.component.css',
})
export class ReservationsComponent implements OnInit, OnDestroy {
  private readonly reservationService = inject(ReservationService);
  private readonly restaurantService = inject(RestaurantService);
  private readonly dialog = inject(MatDialog);
  private readonly toastr = inject(ToastrService);
  private readonly spinner = inject(NgxSpinnerService);
  private readonly router = inject(Router);

  readonly statusLabels = RESERVATION_STATUS_LABELS;
  readonly channelLabels = CHANNEL_LABELS;
  tables: ReservationTable[] = [];
  periods: ServicePeriod[] = [];
  hoursState: ServingHoursState = 'open';
  /** The restaurant's note for a closed date or special hours ("Christmas"). */
  dayNote: string | null = null;
  readonly statusFilters: { value: ReservationStatusFilter; label: string }[] = [
    { value: 'open', label: 'Open' },
    { value: 'pending', label: 'Pending' },
    { value: 'confirmed', label: 'Confirmed' },
    { value: 'seated', label: 'Seated' },
    { value: 'closed', label: 'Closed' },
    { value: 'all', label: 'All' },
  ];

  /** The restaurant's IANA zone. Every time on the book is on its clock. */
  timeZone: string | undefined;
  selectedDate = toServiceDate(new Date());
  /** Until the host picks a date, "today" follows the restaurant's calendar. */
  private dateChosen = false;
  view: ReservationView = 'book';
  statusFilter: ReservationStatusFilter = 'open';
  /** Date, view and status filter live in the URL (?date, ?view, ?status),
   * so a browser reload reopens the same book. ?date is also how Reservation
   * settings links to one day. */
  private urlState = injectUrlViewState();
  searchQuery = '';
  displayLoading = true;
  settings: ReservationSettings | null = null;

  reservations: Reservation[] = [];
  waitlist: Reservation[] = [];
  /** Requests waiting across every date, for the Requests tab's badge (FNF-308). */
  pendingRequestCount = 0;
  groups: BookGroup[] = [];
  timeline: TimelineSlot[] = [];
  selectedId: string | null = null;
  seatTableId = '';

  /** The walk-in typed into Add walk-in, found by phone (FNF-442). */
  walkInReturning: GuestLookup | null = null;
  /** The lookup answered and nobody here has that phone: a first visit. */
  walkInFirstTime = false;
  /** Whom the last walk-in toast was about, so it shows once per guest. */
  private walkInGreeted: string | null = null;
  private readonly walkInPhone$ = new Subject<string>();

  /** The guest card's standing notes form, open for the selected party. */
  notesEditing = false;
  savingNotes = false;
  notesDraft = { allergies: '', preferences: '', occasions: '', tags: '' };

  walkIn = { name: '', phone: '', partySize: 2 };
  /** When the day's data last arrived, and whether a manual refresh is running. */
  loadedAt: Date | null = null;
  refreshing = false;

  /**
   * Why the last load of the day failed (FNF-444): `unavailable` when the
   * reservation API could not be reached, `failed` when it answered with an
   * error. Null once a load works. A failed book is never drawn as an empty
   * day: a host would read that as "nobody booked".
   */
  loadError: 'unavailable' | 'failed' | null = null;
  /** The date whose rows are on screen, set only by a load that worked. */
  private loadedDate: string | null = null;
  /** The next automatic retry while the book can't be loaded. */
  private retry?: Subscription;
  /** How often the book retries on its own while it can't be loaded. */
  static readonly retryMs = 30_000;
  /** SMS opt-in under the walk-in phone field (10DLC registration wording). */
  readonly smsConsent = SMS_CONSENT;
  /** Largest party the host can add as one walk-in. */
  readonly maxWalkInParty = MAX_STAFF_PARTY_SIZE;
  addingWalkIn = false;
  /** Show the phone error only once the host has left the field or tried to add. */
  walkInPhoneTouched = false;
  walkInNameTouched = false;

  // Guided tour. Auto-starts once per browser; the ? button replays it.
  tourOpen = false;
  /** Built once when the tour starts — a getter would hand the child a new
      array every change-detection pass and reset it to step 1. */
  tourSteps: TourStep[] = [];
  private static readonly TOUR_SEEN_KEY = 'fnf.reservations.tourSeen';

  private subs = new Subscription();
  private currentRestaurantDetails: any;

  constructor() {
    this.subs.add(
      this.restaurantService.currentRestaurantDetails$.subscribe((restaurant: any) => {
        this.currentRestaurantDetails = restaurant;
        if (restaurant?.time_zone) {
          this.timeZone = restaurant.time_zone;
          if (!this.dateChosen) {
            this.selectedDate = toServiceDate(new Date(), this.timeZone);
          }
        }
      }),
    );
    this.subs.add(
      this.reservationService.pendingRequestCount$.subscribe(
        (count) => (this.pendingRequestCount = count),
      ),
    );
  }

  ngOnInit(): void {
    // ?date=YYYY-MM-DD opens that day, e.g. from a closed date's warning in
    // Reservation settings (FNF-1181), or after a reload.
    const date = this.urlState.get('date');
    if (date && /^\d{4}-\d{2}-\d{2}$/.test(date)) {
      this.selectedDate = date;
      this.dateChosen = true;
    }
    this.view = this.urlState.pick<ReservationView>(
      'view',
      ['book', 'timeline', 'waitlist', 'requests'],
      'book',
    );
    this.statusFilter = this.urlState.pick<ReservationStatusFilter>(
      'status',
      this.statusFilters.map((f) => f.value),
      'open',
    );
    this.load();
    if (!this.tourSeen()) {
      // Let the first load paint before the spotlight looks for its anchors.
      setTimeout(() => this.startTour(), 1200);
    }
    // Any write anywhere (dialog, panel, settings page) refreshes the book.
    this.subs.add(this.reservationService.changes$.subscribe(() => this.load(true)));
    // Recognise a walk-in by phone as it is typed (FNF-442).
    this.subs.add(
      this.walkInPhone$
        .pipe(
          debounceTime(400),
          map((phone) => usPhoneDigits(phone)),
          distinctUntilChanged(),
          switchMap((digits) =>
            digits.length === 10
              ? this.reservationService.lookupGuest({ phone: digits })
              : of(null),
          ),
        )
        .subscribe((found) => this.recogniseWalkIn(found)),
    );
  }

  ngOnDestroy(): void {
    this.subs.unsubscribe();
    this.retry?.unsubscribe();
  }

  // ------------------------------------------------------------- data

  load(silent = false): void {
    if (!silent) {
      this.displayLoading = true;
    }
    this.reservationService.loadDay(this.selectedDate).subscribe({
      next: ({
        settings,
        reservations,
        waitlist,
        tables,
        periods,
        hoursState,
        dayNote,
        timeZone,
      }) => {
        this.timeZone = timeZone;
        const today = toServiceDate(new Date(), timeZone);
        if (!this.dateChosen && this.selectedDate !== today) {
          // The first guess at "today" used the browser's calendar.
          this.selectedDate = today;
          this.load(silent);
          return;
        }
        this.settings = settings;
        // A queue-only restaurant opens on the waitlist; a timed one never shows it.
        // Mode `off` runs neither and stays on the (empty) Book under its banner.
        if (!this.isViewEnabled(this.view)) {
          this.view = this.isViewEnabled('waitlist') && !this.timedEnabled ? 'waitlist' : 'book';
          this.selectedId = null;
        }
        this.reservations = reservations;
        this.waitlist = waitlist;
        this.tables = tables;
        this.periods = periods;
        this.hoursState = hoursState;
        this.dayNote = dayNote;
        this.rebuild();
        this.displayLoading = false;
        this.refreshing = false;
        this.loadedAt = new Date();
        this.loadedDate = this.selectedDate;
        this.loadError = null;
        this.retry?.unsubscribe();
      },
      error: (err) => {
        // No toast: the banner says it, and stays until the book is back.
        this.loadError = isUnavailable(err) ? 'unavailable' : 'failed';
        this.displayLoading = false;
        this.refreshing = false;
        this.scheduleRetry();
      },
    });
  }

  /**
   * Whether the rows on screen are the selected date's. False after a failed
   * load of a date that never loaded: the book then shows the error panel,
   * not the previous date's rows nor an empty day.
   */
  get dayShown(): boolean {
    return this.loadedDate === this.selectedDate;
  }

  /** Try again now, from the outage banner: the same quiet reload as the tables strip. */
  retryLoad(): void {
    this.refreshTables();
  }

  /**
   * While the book can't be loaded, quietly try again so it comes back on
   * its own once the service recovers, without the host reloading the page.
   */
  private scheduleRetry(): void {
    this.retry?.unsubscribe();
    this.retry = timer(ReservationsComponent.retryMs).subscribe(() => this.load(true));
  }

  /** The calendar's range: a year either side of the restaurant's today. */
  get minDate(): string {
    return toServiceDate(new Date(Date.now() - DATE_RANGE_DAYS * 86_400_000), this.timeZone);
  }

  get maxDate(): string {
    return toServiceDate(new Date(Date.now() + DATE_RANGE_DAYS * 86_400_000), this.timeZone);
  }

  get isToday(): boolean {
    return this.selectedDate === toServiceDate(new Date(), this.timeZone);
  }

  get selected(): Reservation | null {
    if (!this.selectedId) {
      return null;
    }
    return (
      this.reservations.find((r) => r.reservationId === this.selectedId) ??
      this.waitlist.find((r) => r.reservationId === this.selectedId) ??
      null
    );
  }

  /** The host's choice for the left-the-queue list; null follows the day (folded on today). */
  private closedOpen: boolean | null = null;

  /** Walk-ins who left the queue: seated, finished, removed or expired. Newest first. */
  get closedWaitlist(): Reservation[] {
    return this.waitlist
      .filter((w) => CLOSED_STATUSES.includes(w.status) || w.status === 'seated')
      .sort((a, b) => (b.modifiedAt || '').localeCompare(a.modifiedAt || ''));
  }

  get showClosed(): boolean {
    return this.closedOpen ?? !this.isToday;
  }

  toggleClosed(): void {
    this.closedOpen = !this.showClosed;
  }

  /** "2 seated · 1 removed · 2 expired" */
  get closedSummary(): string {
    const counts = new Map<string, number>();
    for (const w of this.closedWaitlist) {
      const label =
        w.status === 'cancelled' ? 'removed' : this.statusLabels[w.status].toLowerCase();
      counts.set(label, (counts.get(label) ?? 0) + 1);
    }
    return [...counts].map(([label, n]) => `${n} ${label}`).join(' · ');
  }

  /** What happened and when, e.g. "At GRO-01 · 6:40 AM" or "The day's queue closed". */
  closedDetail(w: Reservation): string {
    const parts: string[] = [];
    if ((w.status === 'seated' || w.status === 'completed') && this.tableNamesOf(w).length) {
      parts.push(`At ${this.tableLabelOf(w)}`);
    }
    if (w.statusReason) {
      parts.push(w.statusReason);
    }
    parts.push(this.clockOf(w.modifiedAt));
    return parts.filter(Boolean).join(' · ');
  }

  get activeWaitlist(): Reservation[] {
    return this.waitlist.filter(
      (w) => !CLOSED_STATUSES.includes(w.status) && w.status !== 'seated',
    );
  }

  get stats() {
    const open = this.reservations.filter((r) => !CLOSED_STATUSES.includes(r.status));
    return {
      bookings: open.length,
      covers: open.reduce((sum, r) => sum + r.partySize, 0),
      pending: this.reservations.filter((r) => r.status === 'pending').length,
      // Walk-ins seated from the queue stay on the waitlist list, so count both.
      seated: [...this.reservations, ...this.waitlist].filter((r) => r.status === 'seated').length,
      waiting: this.activeWaitlist.length,
    };
  }

  get hasResults(): boolean {
    return this.groups.some((g) => g.items.length > 0);
  }

  private rebuild(): void {
    const query = this.searchQuery.trim().toLowerCase();
    const visible = this.reservations.filter(
      (r) => this.matchesStatus(r) && (!query || this.searchText(r).includes(query)),
    );
    this.groups = this.groupByService(visible);
    this.timeline = this.buildTimeline(visible);
    if (this.selectedId && !this.selected) {
      this.selectedId = null;
    }
  }

  private matchesStatus(r: Reservation): boolean {
    switch (this.statusFilter) {
      case 'all':
        return true;
      case 'open':
        return !CLOSED_STATUSES.includes(r.status);
      case 'closed':
        return CLOSED_STATUSES.includes(r.status);
      default:
        return r.status === this.statusFilter;
    }
  }

  private searchText(r: Reservation): string {
    // Raw and formatted phone, so "4155550100" and "(415) 555" both match.
    const phone = [r.guest.phone, displayUsPhone(r.guest.phone)];
    return [r.guest.name, ...phone, r.guest.email, r.notes, r.occasion, ...this.tableNamesOf(r)]
      .filter(Boolean)
      .join(' ')
      .toLowerCase();
  }

  private groupByService(items: Reservation[]): BookGroup[] {
    const buckets = new Map<string, Reservation[]>();
    for (const period of this.periods) {
      buckets.set(period.name, []);
    }
    const other: Reservation[] = [];
    for (const r of items) {
      const start = new Date(r.startAt);
      const period = this.periods.find(
        (p) =>
          start >= atTime(r.serviceDate, p.start, this.timeZone) &&
          start < periodEnd(r.serviceDate, p, this.timeZone),
      );
      if (period) {
        buckets.get(period.name)!.push(r);
      } else {
        other.push(r);
      }
    }
    const groups = [...buckets.entries()].map(([label, list]) => ({
      label,
      items: list,
      covers: list
        .filter((r) => !CLOSED_STATUSES.includes(r.status))
        .reduce((s, r) => s + r.partySize, 0),
    }));
    if (other.length) {
      groups.push({
        label: 'Other',
        items: other,
        covers: other.reduce((s, r) => s + r.partySize, 0),
      });
    }
    return groups;
  }

  private buildTimeline(items: Reservation[]): TimelineSlot[] {
    const interval = this.settings?.slotIntervalMinutes ?? 15;
    const cap = this.settings?.pacing.maxCoversPerInterval ?? 24;
    const slots: TimelineSlot[] = [];
    for (const period of this.periods) {
      const end = periodEnd(this.selectedDate, period, this.timeZone);
      for (
        let start = atTime(this.selectedDate, period.start, this.timeZone);
        start < end;
        start = addMinutes(start, interval)
      ) {
        const slotEnd = addMinutes(start, interval);
        const inSlot = items.filter((r) => {
          const s = new Date(r.startAt);
          return s >= start && s < slotEnd;
        });
        const covers = inSlot
          .filter((r) => !CLOSED_STATUSES.includes(r.status))
          .reduce((sum, r) => sum + r.partySize, 0);
        slots.push({
          start,
          label: timeLabel(start, this.timeZone),
          items: inSlot,
          covers,
          pacingPct: Math.round((covers / cap) * 100),
        });
      }
    }
    return slots;
  }

  // --------------------------------------------------------- toolbar

  changeView(view: ReservationView): void {
    this.view = view;
    this.selectedId = null;
    this.urlState.set('view', view, 'book');
  }

  setStatusFilter(filter: ReservationStatusFilter): void {
    this.statusFilter = filter;
    this.urlState.set('status', filter, 'open');
    this.rebuild();
  }

  onSearch(): void {
    this.rebuild();
  }

  shiftDate(days: number): void {
    const [y, m, d] = this.selectedDate.split('-').map(Number);
    const next = new Date(y, m - 1, d + days);
    this.selectedDate = toServiceDate(next);
    this.dateChosen = true;
    this.urlState.set('date', this.selectedDate);
    this.selectedId = null;
    this.resetWalkIn();
    this.closedOpen = null;
    this.load();
  }

  goToday(): void {
    this.selectedDate = toServiceDate(new Date(), this.timeZone);
    this.dateChosen = false;
    this.urlState.set('date', null);
    this.selectedId = null;
    this.resetWalkIn();
    this.closedOpen = null;
    this.load();
  }

  onDateChange(): void {
    this.dateChosen = true;
    this.urlState.set('date', this.selectedDate);
    this.selectedId = null;
    this.resetWalkIn();
    this.closedOpen = null;
    this.load();
  }

  // ------------------------------------------------------------ view helpers

  select(r: Reservation | null): void {
    if ((r?.reservationId ?? null) !== this.selectedId) {
      this.notesEditing = false;
    }
    this.selectedId = r?.reservationId ?? null;
    this.seatTableId = this.defaultSeatTable(r);
  }

  // ------------------------------------------------- guest history (FNF-442)

  visitBadgeOf(r: Reservation): VisitBadge | null {
    return visitBadge(r);
  }

  allergyOf(r: Reservation): string | null {
    return allergyWarning(r.guest);
  }

  /** Detail panel only, never the lists. */
  noShowLineOf(r: Reservation): string | null {
    return noShowLine(r.guest);
  }

  hasNotes(r: Reservation): boolean {
    return hasStandingNotes(r.guest.standingNotes);
  }

  historySupported(r: Reservation): boolean {
    return historySupported(r.guest);
  }

  /** A warning instead of silence when a save lost the notes it carried. */
  private warnIfNotesDropped(sent: StandingNotesChange | null | undefined, saved: Reservation) {
    if (standingNotesDropped(sent, saved)) {
      this.toastr.warning(NOTES_NOT_KEPT, 'Guest notes not saved', { timeOut: 10_000 });
      return true;
    }
    return false;
  }

  /** "8 Oct" this year, "8 Oct 2025" before; the restaurant's calendar. */
  lastVisitOf(r: Reservation): string | null {
    const iso = r.guest.lastVisitAt;
    if (!iso) {
      return null;
    }
    const d = new Date(iso);
    const thisYear =
      formatInZone(d, this.timeZone, { year: 'numeric' }) ===
      formatInZone(new Date(), this.timeZone, { year: 'numeric' });
    return formatInZone(d, this.timeZone, {
      day: 'numeric',
      month: 'short',
      ...(thisYear ? {} : { year: 'numeric' }),
    });
  }

  startNotes(r: Reservation): void {
    const n = r.guest.standingNotes;
    this.notesDraft = {
      allergies: n?.allergies ?? '',
      preferences: n?.preferences ?? '',
      occasions: n?.occasions ?? '',
      tags: (n?.tags ?? []).join(', '),
    };
    this.notesEditing = true;
  }

  cancelNotes(): void {
    this.notesEditing = false;
  }

  /** Only the fields the host changed go up, so two hosts don't overwrite each other. */
  saveNotes(r: Reservation): void {
    const change = standingNotesChange(r.guest.standingNotes, {
      ...this.notesDraft,
      tags: parseTags(this.notesDraft.tags),
    });
    if (!change) {
      this.notesEditing = false;
      return;
    }
    this.savingNotes = true;
    this.reservationService.saveStandingNotes(r.reservationId, change).subscribe({
      next: (saved) => {
        this.savingNotes = false;
        this.notesEditing = false;
        if (!this.warnIfNotesDropped(change, saved)) {
          this.toastr.success(`Kept for ${r.guest.name}'s next visits too.`, 'Guest notes saved');
        }
      },
      error: (err) => {
        this.savingNotes = false;
        this.fail(err);
      },
    });
  }

  /** The booking has a table, but it isn't free for this party right now. */
  bookedTableTaken(r: Reservation): boolean {
    const free = this.freeTablesFor(r);
    return r.tableIds.length > 0 && !r.tableIds.some((id) => free.some((t) => t.id === id));
  }

  /**
   * The table the Seat picker starts on: the booked table when it is free for
   * this party right now, else the smallest free one. Never a table missing
   * from the picker, which would leave the select blank.
   */
  private defaultSeatTable(r: Reservation | null): string {
    const free = this.freeTablesFor(r);
    const booked = r?.tableIds.find((id) => free.some((t) => t.id === id));
    return booked ?? free[0]?.id ?? '';
  }

  timeOf(r: Reservation): string {
    return timeLabel(new Date(r.startAt), this.timeZone);
  }

  endOf(r: Reservation): string {
    return timeLabel(new Date(r.endAt), this.timeZone);
  }

  /**
   * The booking's tables by the restaurant's own names ("T3 + T4"), never a
   * raw table id: reservation-service may have stored the id as the name.
   */
  tableNamesOf(r: Reservation): string[] {
    return bookingTableNames(r.tableIds, r.tableNames, this.tables);
  }

  tableLabelOf(r: Reservation): string {
    return tableNamesLabel(this.tableNamesOf(r));
  }

  /** "8 Oct, 6:30 PM" on the restaurant's clock. */
  stampOf(iso: string): string {
    if (!iso) {
      return '';
    }
    return formatInZone(new Date(iso), this.timeZone, {
      day: 'numeric',
      month: 'short',
      hour: 'numeric',
      minute: '2-digit',
    });
  }

  /** A guest phone as "(555) 123-4567", like the staff list. */
  phoneOf(phone: string | null | undefined): string {
    return displayUsPhone(phone);
  }

  /** "6:30 PM" on the restaurant's clock. */
  clockOf(iso: string): string {
    return !iso ? '' : timeLabel(new Date(iso), this.timeZone);
  }

  channelIcon(channel: ReservationChannel): string {
    return CHANNEL_ICONS[channel] ?? 'bx-desktop';
  }

  riskLevel(r: Reservation): 'low' | 'medium' | 'high' | null {
    const score = r.risk?.noShowScore;
    if (score == null || CLOSED_STATUSES.includes(r.status) || r.status === 'seated') {
      return null;
    }
    return score >= 0.35 ? 'high' : score >= 0.2 ? 'medium' : 'low';
  }

  waitedMinutes(w: Reservation): number {
    return Math.max(0, Math.round((Date.now() - new Date(w.createdAt).getTime()) / 60_000));
  }

  expiresInMinutes(w: Reservation): number | null {
    if (!w.expiresAt) {
      return null;
    }
    return Math.max(0, Math.ceil((new Date(w.expiresAt).getTime() - Date.now()) / 60_000));
  }

  /**
   * A walk-in from a day that has ended. Its queue is closed: no text, no
   * seat. reservation-service refuses both too, and expires these on list.
   */
  queueClosedFor(r: Reservation | null): boolean {
    return !!r && r.kind === 'waitlist' && r.serviceDate < toServiceDate(new Date(), this.timeZone);
  }

  /** The bell's tooltip: why it's off, or what it does. */
  notifyHint(w: Reservation): string {
    if (this.queueClosedFor(w)) {
      return 'This day has ended';
    }
    return w.guest.phone ? 'Text: table ready' : 'No phone number, so call by name';
  }

  /** Why a timed booking can't be seated now, or null when it can. */
  seatBlockReason(r: Reservation): string | null {
    if (r.kind !== 'reservation') {
      return null;
    }
    const now = Date.now();
    if (now >= new Date(r.endAt).getTime()) {
      return "This booking's time has passed";
    }
    const from = new Date(new Date(r.startAt).getTime() - SEAT_EARLY_MINUTES * 60_000);
    return now < from.getTime() ? `Can be seated from ${this.momentLabel(from)}` : null;
  }

  /**
   * Today's booking whose guest is here before the seat window opens. Seating
   * them moves the booking to now (reservation-service, `early: true`).
   */
  canSeatEarly(r: Reservation | null): boolean {
    if (!r || r.kind !== 'reservation' || !canTransition(r.status, 'seated')) {
      return false;
    }
    const opens = new Date(r.startAt).getTime() - SEAT_EARLY_MINUTES * 60_000;
    return Date.now() < opens && r.serviceDate === toServiceDate(new Date(), this.timeZone);
  }

  /** Why No-show isn't available yet: it waits for the grace period after the start. */
  noShowBlockReason(r: Reservation): string | null {
    const grace = this.settings?.policy.noShowGraceMinutes ?? 15;
    const from = new Date(new Date(r.startAt).getTime() + grace * 60_000);
    return Date.now() < from.getTime() ? `Available from ${this.momentLabel(from)}` : null;
  }

  /** "12:30 PM" today, "Mon 12 Oct, 12:30 PM" on another day; restaurant's clock. */
  private momentLabel(d: Date): string {
    const sameDay = toServiceDate(d, this.timeZone) === toServiceDate(new Date(), this.timeZone);
    if (sameDay) {
      return timeLabel(d, this.timeZone);
    }
    const day = formatInZone(d, this.timeZone, {
      weekday: 'short',
      day: 'numeric',
      month: 'short',
    });
    return `${day}, ${timeLabel(d, this.timeZone)}`;
  }

  can(r: Reservation | null, to: ReservationStatus): boolean {
    return !!r && canTransition(r.status, to);
  }

  /** Tables free right now that seat the party, smallest first. */
  /**
   * Tables that seat the party and are free from now until its turn ends:
   * nobody sat there (a seated booking or a seated walk-in) and no open
   * booking starts there before then. reservation-service checks the same
   * when seating and answers 409 on a clash.
   */
  freeTablesFor(r: Reservation | null): ReservationTable[] {
    if (!r) {
      return [];
    }
    const start = Date.now();
    const booked = new Date(r.endAt).getTime() - new Date(r.startAt).getTime();
    const end = this.canSeatEarly(r)
      ? start + booked // seated early: the booking moves to now, same length
      : r.kind === 'waitlist' || !r.endAt || new Date(r.endAt).getTime() <= start
        ? start + this.turnMinutes(r.partySize) * 60_000
        : new Date(r.endAt).getTime();
    const busy = new Set(
      this.tableUses()
        .filter((u) => u.reservationId !== r.reservationId && u.start < end && start < u.end)
        .flatMap((u) => u.tableIds),
    );
    return this.tables
      .filter((t) => t.seats >= r.partySize && !busy.has(t.id))
      .sort((a, b) => a.seats - b.seats);
  }

  /**
   * Reload the day, so the tables strip picks up seats and closes other
   * staff made on their own devices. Quiet: no page spinner.
   */
  refreshTables(): void {
    if (this.refreshing) {
      return;
    }
    this.refreshing = true;
    this.load(true);
  }

  get loadedAtLabel(): string {
    return this.loadedAt ? timeLabel(this.loadedAt, this.timeZone) : '';
  }

  /** What 🪑 on a queue row would do, said before it does it. */
  seatHint(w: Reservation): string {
    if (this.queueClosedFor(w)) {
      return 'This day has ended';
    }
    const table = this.suggestTable(w);
    return table ? `Seat at ${table.name}` : 'No free table fits this party';
  }

  /**
   * Every table on the day, for the strip above the queue: free, in use
   * (by whom, and until when), or booked within the next turn.
   */
  get tableStrip(): {
    table: ReservationTable;
    state: 'free' | 'busy' | 'soon';
    note: string;
  }[] {
    const now = Date.now();
    const horizon = now + this.turnMinutes(2) * 60_000;
    const uses = this.tableUses();
    return [...this.tables]
      .sort((a, b) => a.name.localeCompare(b.name, undefined, { numeric: true }))
      .map((table) => {
        const here = uses.filter((u) => u.tableIds.includes(table.id));
        const inUse = here.find((u) => u.seated && u.start <= now && now < u.end);
        if (inUse) {
          const until = timeLabel(new Date(inUse.end), this.timeZone);
          return { table, state: 'busy' as const, note: `${inUse.name} · until ~${until}` };
        }
        const next = here
          .filter((u) => !u.seated && u.start >= now && u.start < horizon)
          .sort((a, b) => a.start - b.start)[0];
        if (next) {
          const at = timeLabel(new Date(next.start), this.timeZone);
          return { table, state: 'soon' as const, note: `Booked ${at}` };
        }
        return { table, state: 'free' as const, note: 'Free' };
      });
  }

  /** Table use from seated parties (both lists) and open timed bookings. */
  private tableUses(): {
    reservationId: string;
    name: string;
    tableIds: string[];
    start: number;
    end: number;
    seated: boolean;
  }[] {
    const seated = [...this.reservations, ...this.waitlist].filter(
      (x) => x.status === 'seated' && x.tableIds.length,
    );
    const booked = this.reservations.filter(
      (x) => ['pending', 'confirmed'].includes(x.status) && x.tableIds.length,
    );
    return [
      ...seated.map((x) => ({ ...this.useOf(x), seated: true })),
      ...booked.map((x) => ({ ...this.useOf(x), seated: false })),
    ];
  }

  private useOf(x: Reservation) {
    return {
      reservationId: x.reservationId,
      name: x.guest.name,
      tableIds: x.tableIds,
      start: new Date(x.startAt).getTime(),
      end: new Date(x.endAt).getTime(),
    };
  }

  private turnMinutes(partySize: number): number {
    return this.settings ? turnTimeFor(this.settings, partySize) : 90;
  }

  suggestTable(r: Reservation | null): ReservationTable | undefined {
    return this.freeTablesFor(r)[0];
  }

  // ------------------------------------------------------------- tour

  private buildTourSteps(): TourStep[] {
    const mode = this.settings?.mode ?? 'timed';
    const modeLine: Record<string, string> = {
      off: 'Reservations are currently off. Pick a mode in Reservation settings to start.',
      timed: 'Timed mode: guests book a slot and get a table for the turn time you set.',
      request: 'Request mode: every booking arrives as a request you confirm or decline.',
      waitlist: 'Waitlist mode: walk-ins join a queue; there are no timed slots.',
      hybrid: 'Hybrid mode: timed slots and a same-day queue share the same tables.',
    };
    const steps: TourStep[] = [
      {
        target: 'date',
        title: 'One service day at a time',
        body: 'Everything on this page is for the selected day. Use the arrows or the calendar to move between days, and Today to jump back.',
      },
      {
        target: 'views',
        context: 'book',
        title: 'Book: the day by service',
        body: 'The Book groups the day’s bookings by service period. Click a guest to open their card on the right, where every action lives.',
      },
      {
        target: 'views',
        context: 'timeline',
        title: 'Timeline: pacing at a glance',
        body: 'Each bar is one slot interval showing covers against your pacing cap. Full bars mean the kitchen is at capacity for that interval.',
      },
      {
        target: 'views',
        context: 'waitlist',
        title: 'Waitlist: the walk-in queue',
        body: 'Parties waiting for a table, in order, with their quoted wait. Text them when a table is ready, then seat them onto a table.',
      },
      {
        target: 'walkin',
        context: 'waitlist',
        title: 'Add a walk-in',
        body: 'Name, phone and party size are enough. The phone is what the "table ready" text goes to; guests can also join from the Fynkle app.',
      },
      {
        target: 'search',
        title: 'Find a guest',
        body: 'Search by name, phone, email, note or table. The status chips next to it narrow the Book to open, pending, confirmed, seated or closed bookings.',
      },
      {
        target: 'stats',
        title: 'The day in numbers',
        body: this.queueOnly
          ? 'Parties seated right now from the queue, and how many are still waiting.'
          : 'Open bookings and covers, requests still to confirm, parties seated right now and how many are waiting in the queue.',
      },
      {
        target: 'add',
        title: 'New reservation',
        body: 'Book a guest who called or walked in. Pick party size and date, then one of the times the floor can actually honour; large parties can be left as requests.',
      },
      {
        target: 'panel',
        context: 'book',
        placement: 'left',
        title: 'Working a booking',
        body: 'Select any guest to open their card. From there: Confirm or Decline a request, Seat onto a table, Close the table when they leave, mark a No-show after the grace period, Edit, or Cancel. Only legal moves are shown.',
      },
      {
        target: 'settings',
        title: 'Reservation settings',
        body: `${modeLine[mode] ?? modeLine['timed']} Settings also hold turn times, pacing, party limits, the booking window, cancel cutoff and no-show grace.`,
      },
      {
        target: 'help',
        title: 'Need this again?',
        body: 'Click the question mark any time to replay the tour. Keyboard: → next, ← back, Esc to close.',
      },
    ];
    // Skip the steps for views this mode doesn't run, and those pointing at
    // the view switcher when it is hidden.
    return steps.filter(
      (s) =>
        (!(s.context === 'book' || s.context === 'timeline' || s.context === 'waitlist') ||
          this.isViewEnabled(s.context)) &&
        (s.target !== 'views' || this.showViewTabs) &&
        (s.target !== 'add' || this.timedEnabled),
    );
  }

  startTour(): void {
    this.tourSteps = this.buildTourSteps();
    this.tourOpen = true;
  }

  onTourStep(step: TourStep): void {
    if (step.context === 'book' || step.context === 'timeline' || step.context === 'waitlist') {
      this.view = step.context;
    }
    // The guest card is what this step is about, so open one if the host has
    // not picked anybody — otherwise the step points at nothing.
    if (step.target === 'panel' && !this.selected) {
      const first = this.groups.flatMap((g) => g.items)[0] ?? this.reservations[0];
      if (first) {
        this.select(first);
      }
    }
  }

  endTour(_reason: 'done' | 'skipped'): void {
    this.tourOpen = false;
    try {
      localStorage.setItem(ReservationsComponent.TOUR_SEEN_KEY, '1');
    } catch {
      /* private mode: the tour simply auto-starts again next time */
    }
  }

  private tourSeen(): boolean {
    try {
      return localStorage.getItem(ReservationsComponent.TOUR_SEEN_KEY) === '1';
    } catch {
      return true;
    }
  }

  // ---------------------------------------------------------- actions

  /** Mode `waitlist`: the restaurant takes walk-ins only, no timed bookings. */
  get queueOnly(): boolean {
    return this.settings?.mode === 'waitlist';
  }

  /** Modes that take bookings ahead of time (`timed`, `request`, `hybrid`). */
  get timedEnabled(): boolean {
    const mode = this.settings?.mode;
    return mode === 'timed' || mode === 'request' || mode === 'hybrid';
  }

  /** Modes that run a walk-in queue (`waitlist`, `hybrid`), as reservation-service allows. */
  get waitlistEnabled(): boolean {
    const mode = this.settings?.mode;
    return mode === 'waitlist' || mode === 'hybrid';
  }

  /**
   * Whether the mode runs a view: Book and Timeline need timed bookings, the
   * Waitlist needs a queue. Before settings load, only the default Book shows.
   */
  isViewEnabled(view: ReservationView): boolean {
    if (!this.settings) {
      return view !== 'waitlist';
    }
    // Requests (FNF-308) arise wherever bookings are taken ahead of time.
    return view === 'waitlist' ? this.waitlistEnabled : this.timedEnabled;
  }

  /** The view switcher, only when there is more than one view to switch to. */
  get showViewTabs(): boolean {
    return this.isViewEnabled('book');
  }

  /** "Thu, Dec 25", for the closed-date banner. */
  get selectedDateLabel(): string {
    return dateLabel(this.selectedDate);
  }

  /** Weekday of the selected date, for the "closed on …" banner. */
  get selectedWeekday(): string {
    const [y, m, d] = this.selectedDate.split('-').map(Number);
    return new Date(y, m - 1, d).toLocaleDateString('en-US', { weekday: 'long' });
  }

  newReservation(): void {
    if (this.settings?.mode === 'off') {
      this.toastr.info(
        'Choose a reservation mode in Reservation settings to start taking bookings.',
        'Reservations are off',
      );
      return;
    }
    if (this.queueOnly) {
      this.toastr.info(
        'This restaurant runs a walk-in queue. Add the party to the waitlist, or switch to timed or hybrid mode in Reservation settings to take bookings ahead of time.',
        'Walk-in queue',
      );
      this.changeView('waitlist');
      return;
    }
    const ref = this.dialog.open(AddReservationModalComponent, {
      width: '720px',
      maxWidth: '95vw',
      data: { date: this.selectedDate, settings: this.settings, timeZone: this.timeZone },
    });
    ref.afterClosed().subscribe((result) => {
      if (!result) {
        return;
      }
      this.spinner.show();
      this.reservationService.create(result).subscribe({
        next: (created) => {
          this.spinner.hide();
          this.toastr.success(
            `${created.guest.name} · party of ${created.partySize} at ${this.timeOf(created)}`,
            created.status === 'pending' ? 'Request added' : 'Reservation confirmed',
          );
          this.warnIfNotesDropped(result.standingNotes, created);
          this.reportGuestNotified(created);
          this.selectedDate = created.serviceDate;
          this.selectedId = created.reservationId;
        },
        error: (err) => this.fail(err),
      });
    });
  }

  edit(r: Reservation): void {
    const ref = this.dialog.open(AddReservationModalComponent, {
      width: '720px',
      maxWidth: '95vw',
      data: {
        reservation: r,
        date: r.serviceDate,
        settings: this.settings,
        timeZone: this.timeZone,
      },
    });
    ref.afterClosed().subscribe((result) => {
      if (!result) {
        return;
      }
      this.spinner.show();
      this.reservationService
        .update(r.reservationId, {
          startAt: result.startAt,
          partySize: result.partySize,
          guest: result.guest,
          notes: result.notes,
          occasion: result.occasion,
          tableIds: result.tableIds?.length ? result.tableIds : undefined,
          standingNotes: result.standingNotes,
        })
        .subscribe({
          next: (updated) => {
            this.spinner.hide();
            this.toastr.success('Reservation updated', 'Saved');
            this.warnIfNotesDropped(result.standingNotes, updated);
            this.reportGuestNotified(updated);
            this.selectedDate = updated.serviceDate;
          },
          error: (err) => this.fail(err),
        });
    });
  }

  confirm(r: Reservation): void {
    this.transition(r, 'confirmed', 'Reservation confirmed');
  }

  /** Decline with an optional reason the guest is told (FNF-308). */
  decline(r: Reservation): void {
    askDeclineReason(this.dialog, {
      guestName: r.guest.name,
      partySize: r.partySize,
      // The Requests queue spans dates, so name the day when it isn't today.
      when: this.momentLabel(new Date(r.startAt)),
    }).subscribe(
      (result) =>
        result && this.transition(r, 'declined', 'Request declined', result.reason || undefined),
    );
  }

  /** From the Requests queue to the booking on its own day (FNF-308). */
  openRequest(r: Reservation): void {
    this.changeView('book');
    if (r.serviceDate !== this.selectedDate) {
      this.selectedDate = r.serviceDate;
      this.dateChosen = true;
      this.urlState.set('date', this.selectedDate);
      this.resetWalkIn();
      this.closedOpen = null;
    }
    this.notesEditing = false;
    this.selectedId = r.reservationId;
    // Another day, or a request newer than the loaded day: rebuild() keeps
    // the selection once the day arrives with it.
    if (!this.selected) {
      this.load();
    }
  }

  /** Time left to answer a pending request, e.g. "expires in 1h 20m" (FNF-308). */
  requestDeadlineOf(r: Reservation): string | null {
    return r.status === 'pending' ? requestDeadlineLabel(r.expiresAt) : null;
  }

  /** "Expired — not answered in time" or "Declined: {reason}" (FNF-308). */
  declineReasonOf(r: Reservation): string | null {
    return declineReasonLabel(r);
  }

  cancel(r: Reservation): void {
    this.ask(
      `Cancel <span class="text-black">${r.guest.name}</span>'s ${
        r.kind === 'waitlist' ? 'waitlist entry' : 'reservation'
      }? The guest will be notified and the ${r.kind === 'waitlist' ? 'spot' : 'table'} released.`,
      'Yes, cancel',
    ).subscribe((ok) => ok && this.transition(r, 'cancelled', 'Cancelled'));
  }

  noShow(r: Reservation): void {
    this.ask(
      `Mark <span class="text-black">${r.guest.name}</span> as a no-show? This counts against their history.`,
      'Mark no-show',
    ).subscribe((ok) => ok && this.transition(r, 'no_show', 'Marked as no-show'));
  }

  complete(r: Reservation): void {
    this.transition(r, 'completed', 'Table closed');
  }

  seat(r: Reservation): void {
    const tableId = this.seatTableId || this.suggestTable(r)?.id;
    if (!tableId) {
      this.toastr.warning('No free table seats this party right now.', 'Nothing free');
      return;
    }
    this.spinner.show();
    this.reservationService.seat(r.reservationId, [tableId], this.canSeatEarly(r)).subscribe({
      next: (seated) => {
        this.spinner.hide();
        this.toastr.success(
          `${seated.guest.name} seated at ${this.tableLabelOf(seated)}`,
          'Seated',
        );
        // The moment the table's server needs to know (FNF-1187).
        const allergy = allergyWarning(seated.guest);
        if (allergy) {
          this.toastr.warning(`Tell the server: ${allergy}.`, 'Allergy noted', {
            timeOut: 10_000,
          });
        }
      },
      error: (err) => this.fail(err),
    });
  }

  /** Table ready, then tell the host whether the guest was really texted. */
  notify(w: Reservation): void {
    this.spinner.show();
    this.reservationService.transition(w.reservationId, 'notified').subscribe({
      next: (updated) => {
        this.spinner.hide();
        const phone = this.phoneOf(w.guest.phone);
        switch (updated.smsStatus) {
          case 'sent':
          case 'duplicate':
            this.toastr.success(`Text sent to ${phone}.`, 'Table ready');
            break;
          case 'opted_out':
            this.toastr.warning(
              `${w.guest.name} has opted out of texts. Call them by name.`,
              'Table ready',
            );
            break;
          case 'no_phone':
            this.toastr.info(`No phone on file. Call ${w.guest.name} by name.`, 'Table ready');
            break;
          default:
            // disabled, invalid_phone, failed, unavailable, or an older API.
            this.toastr.warning(
              `The text didn't go out. Call ${w.guest.name} by name.`,
              'Table ready',
            );
        }
      },
      error: (err) => this.fail(err),
    });
  }

  backToWaiting(w: Reservation): void {
    this.transition(w, 'waitlisted', 'Back in the queue');
  }

  /**
   * Add walk-in stays disabled until the name has letters, while the optional
   * phone is incomplete, and while one is being added.
   */
  get canAddWalkIn(): boolean {
    return (
      !this.addingWalkIn && this.walkInNameValid && this.walkInPhoneValid && this.walkInPartyValid
    );
  }

  /** A whole number from 1 to maxWalkInParty. */
  get walkInPartyValid(): boolean {
    const size = Number(this.walkIn.partySize);
    return Number.isInteger(size) && size >= 1 && size <= this.maxWalkInParty;
  }

  /** The walk-in form is for today's queue; another date starts it afresh. */
  private resetWalkIn(): void {
    this.walkIn = { name: '', phone: '', partySize: 2 };
    this.walkInPhoneTouched = false;
    this.walkInNameTouched = false;
    this.walkInReturning = null;
    this.walkInFirstTime = false;
    this.walkInGreeted = null;
    this.walkInPhone$.next('');
  }

  /**
   * Today's booking already made under the walk-in's phone: they should be
   * seated on it, not queued again. Read from the day already loaded, so it
   * works whatever the server, and the walk-in form is today's only.
   */
  get walkInBooking(): Reservation | null {
    return this.isToday ? bookingForPhone(this.walkIn.phone, this.reservations) : null;
  }

  /** "2 bookings · 3 visits · last visit 8 Oct". */
  get walkInSummary(): string {
    const g = this.walkInReturning;
    if (!g) {
      return '';
    }
    const last = g.lastVisitAt
      ? ` · last visit ${formatInZone(new Date(g.lastVisitAt), this.timeZone, {
          day: 'numeric',
          month: 'short',
        })}`
      : '';
    return returningGuestSummary(g) + last;
  }

  /**
   * One toast per guest: "Already booked" when today's book has them (the
   * thing not to miss), else "Returning guest" with their allergy.
   */
  private recogniseWalkIn(found: GuestLookup | null): void {
    this.walkInReturning = found?.known ? found : null;
    this.walkInFirstTime = !!found && !found.known;
    const booked = this.walkInBooking;
    const key = booked?.reservationId ?? this.walkInReturning?.guestKey ?? null;
    if (!key || key === this.walkInGreeted) {
      return;
    }
    this.walkInGreeted = key;
    const allergy =
      (booked && allergyWarning(booked.guest)) ||
      this.walkInReturning?.standingNotes.allergies?.trim() ||
      null;
    const allergyLine = allergy ? ` Allergy: ${allergy}.` : '';
    if (booked) {
      const table = this.tableLabelOf(booked);
      this.toastr.warning(
        `${booked.guest.name} has a booking today at ${this.timeOf(booked)}${
          table ? ` (${table})` : ''
        }. Open it to seat them, rather than adding a walk-in.${allergyLine}`,
        'Already booked',
        { timeOut: 10_000 },
      );
    } else if (this.walkInReturning) {
      this.toastr.info(
        `${returningGuestSummary(this.walkInReturning)} here.${allergyLine}`,
        'Returning guest',
      );
    }
  }

  /** From the walk-in form to the guest's own booking, to seat it there. */
  openBooking(r: Reservation): void {
    if (this.isViewEnabled('book')) {
      this.changeView('book');
    }
    this.select(r);
  }

  /** Entered and not only numbers or symbols, as in New reservation. */
  get walkInNameValid(): boolean {
    return isGuestName(this.walkIn.name.trim());
  }

  /** Optional, but a complete US number when given, as in New reservation. */
  get walkInPhoneValid(): boolean {
    return !this.walkIn.phone || US_PHONE_PATTERN.test(this.walkIn.phone);
  }

  /** Formats the phone as it is typed, like Add staff's Contact No. */
  onWalkInPhoneInput(event: Event): void {
    const input = event.target as HTMLInputElement;
    this.walkIn.phone = formatUsPhone(input.value);
    input.value = this.walkIn.phone;
    this.walkInPhone$.next(this.walkIn.phone);
  }

  addWalkIn(): void {
    const name = this.walkIn.name.trim();
    if (!isGuestName(name) || !this.walkInPartyValid) {
      this.toastr.warning('A name and party size are needed.', 'Incomplete');
      return;
    }
    if (!this.walkInPhoneValid) {
      this.walkInPhoneTouched = true;
      return;
    }
    this.addingWalkIn = true;
    this.reservationService
      .joinWaitlist({
        partySize: Number(this.walkIn.partySize),
        guest: { name, phone: usPhoneDigits(this.walkIn.phone) || null, visits: 0 },
      })
      .subscribe({
        next: (entry) => {
          this.addingWalkIn = false;
          this.resetWalkIn();
          this.toastr.success(
            `Quoted ~${entry.quotedWaitMinutes} min, position ${entry.position}`,
            'Added to waitlist',
          );
          this.selectedId = entry.reservationId;
        },
        error: (err) => {
          this.addingWalkIn = false;
          this.fail(err);
        },
      });
  }

  openFloorPlan(): void {
    this.router.navigate(['/main/table'], { queryParams: { view: 'layout' } });
  }

  private transition(
    r: Reservation,
    to: ReservationStatus,
    message: string,
    reason?: string,
  ): void {
    this.spinner.show();
    this.reservationService.transition(r.reservationId, to, reason).subscribe({
      next: (updated) => {
        this.spinner.hide();
        this.toastr.success(message, 'Done');
        this.reportGuestNotified(updated);
      },
      error: (err) => this.fail(err),
    });
  }

  /**
   * How the guest heard about a booking change (FNF-1130), as a second toast.
   * Nothing when the change wasn't news to them or every channel was off.
   */
  private reportGuestNotified(r: Reservation): void {
    const line = guestNotifiedLine(r.smsStatus, r.emailStatus, {
      email: r.guest.email,
      phone: this.phoneOf(r.guest.phone),
    });
    if (!line) {
      return;
    }
    if (line.ok) {
      this.toastr.success(line.text, line.title);
    } else {
      this.toastr.warning(line.text, line.title);
    }
  }

  private ask(message: string, ok: string) {
    return this.dialog
      .open(ConfirmationDialogComponent, {
        data: { message, buttonText: { ok, cancel: 'No, keep it' } },
      })
      .afterClosed();
  }

  private fail(err: unknown): void {
    this.spinner.hide();
    const message =
      err instanceof Error
        ? err.message
        : 'Unable to complete your action. Please try again later.';
    this.toastr.error(message, 'Error');
  }
}
